import React, { useState, useEffect, useRef } from 'react';
import './ChatBox.css';

const ChatBox = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState('');
    const [chatId, setChatId] = useState(localStorage.getItem('chatId') || '');
    const [lastSeenCount, setLastSeenCount] = useState(parseInt(localStorage.getItem('lastSeenCount') || '0'));
    const [hasNotification, setHasNotification] = useState(false);
    
    const messagesEndRef = useRef(null);
    const notificationSound = useRef(new Audio('https://assets.mixkit.co/active_storage/sfx/2354/2354-preview.mp3'));
    const backendUrl = "https://e-commerce-backend-h191.onrender.com";

    useEffect(() => {
        if (!chatId) {
            const newId = 'chat_' + Math.random().toString(36).substr(2, 9);
            localStorage.setItem('chatId', newId);
            setChatId(newId);
        }
    }, [chatId]);

    // Constant polling to check for new messages even when closed
    useEffect(() => {
        if (chatId) {
            fetchHistory();
            const interval = setInterval(fetchHistory, 5000);
            return () => clearInterval(interval);
        }
    }, [chatId]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Handle notifications logic
    useEffect(() => {
        if (isOpen) {
            setHasNotification(false);
            setLastSeenCount(messages.length);
            localStorage.setItem('lastSeenCount', messages.length.toString());
        } else if (messages.length > lastSeenCount) {
            const lastMessage = messages[messages.length - 1];
            if (lastMessage && lastMessage.sender === 'admin') {
                setHasNotification(true);
                // Attempt to play sound (browsers may block until first user interaction)
                notificationSound.current.play().catch(err => console.log("Sound play blocked:", err));
            }
        }
    }, [messages, isOpen, lastSeenCount]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }

    const fetchHistory = async () => {
        try {
            const response = await fetch(`${backendUrl}/chat/history/${chatId}`);
            const data = await response.json();
            if (data.success) {
                setMessages(data.messages);
            }
        } catch (error) {
            console.error("Error fetching history:", error);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!inputText.trim()) return;

        const newMessage = { sender: 'customer', text: inputText };
        
        // Optimistic update
        const updatedMessages = [...messages, newMessage];
        setMessages(updatedMessages);
        setLastSeenCount(updatedMessages.length);
        localStorage.setItem('lastSeenCount', updatedMessages.length.toString());
        
        const textToSend = inputText;
        setInputText('');

        try {
            await fetch(`${backendUrl}/chat/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ chatId, text: textToSend })
            });
            fetchHistory();
        } catch (error) {
            console.error("Error sending message:", error);
        }
    };

    return (
        <div className="chat-container">
            <button className={`chat-toggle-btn ${hasNotification ? 'has-notification' : ''}`} onClick={() => setIsOpen(!isOpen)}>
                {isOpen ? '✕' : '💬'}
                {hasNotification && !isOpen && <span className="notification-dot"></span>}
            </button>
            {isOpen && (
                <div className="chat-window">
                    <div className="chat-header">
                        <h3>Sneaker Production Support</h3>
                    </div>
                    <div className="chat-messages">
                        {messages.length === 0 && <p className="empty-chat">How can we help you today?</p>}
                        {messages.map((msg, index) => (
                            <div key={index} className={`message-bubble ${msg.sender}`}>
                                {msg.text}
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>
                    <form className="chat-input" onSubmit={handleSendMessage}>
                        <input 
                            type="text" 
                            placeholder="Type a message..." 
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                        />
                        <button type="submit">➤</button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default ChatBox;
